import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export const MA = `${process.env.REACT_APP_BACKEND_URL}/api/ads/meta`;
const CR = `${process.env.REACT_APP_BACKEND_URL}/api/creatives`;
const NEEDS_ASSET = (t) => t !== "catalog_sales";

export const Field = ({ label, children }) => <label className="block space-y-1"><span className="text-xs text-zinc-500">{label}</span>{children}</label>;

export const AssetPicker = ({ value, onChange }) => {
  const on = (id) => (Array.isArray(value) ? value.includes(id) : value === id);
  const [items, setItems] = useState(null);
  useEffect(() => { axios.get(CR, { params: { limit: 40 } }).then((r) => setItems(r.data.items || [])).catch(() => setItems([])); }, []);
  if (!items) return <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />;
  return (
    <div className="grid max-h-48 grid-cols-6 gap-1.5 overflow-y-auto" data-testid="meta-asset-picker">
      {items.map((a) => (
        <button key={a.id} type="button" data-testid={`meta-asset-${a.id}`} onClick={() => onChange(a.id)} className={`relative overflow-hidden rounded border-2 ${on(a.id) ? "border-amber-500" : "border-transparent"}`}>
          {a.kind === "video" ? <video src={`${a.url}#t=0.5`} muted preload="metadata" className="aspect-square w-full bg-black object-cover" /> : <img src={`${a.url}?w=200`} alt="" loading="lazy" className="aspect-square w-full object-cover" />}
          {a.kind === "video" && <span className="absolute bottom-0.5 left-0.5 rounded bg-black/70 px-1 text-[9px] text-white">video</span>}
        </button>))}
    </div>
  );
};

export const MetaWizard = ({ init, onClose }) => {
  const [opt, setOpt] = useState(null);
  const [f, setF] = useState({ campaign_type: init?.campaign_type || "sales_creative", name: init?.name || (init?.theme ? `BragusApp — ${init.theme}`.slice(0, 100) : ""), daily_ron: init?.daily_ron || 40,
    link: init?.link || "https://www.bragus.ro", asset_id: init?.asset_id || "", text: init?.text || "", headline: init?.headline || "", description: init?.description || "", cta: "SHOP_NOW" });
  const [links, setLinks] = useState([]);
  useEffect(() => { axios.get(`${MA}/link-options`).then((r) => setLinks(r.data.items)).catch(() => {}); }, []);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  useEffect(() => { axios.get(`${MA}/create-options`).then((r) => setOpt(r.data)).catch(() => setOpt({ types: [], ctas: [] })); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const submit = async () => {
    setBusy(true);
    try { const r = await axios.post(`${MA}/campaigns`, { ...f, daily_ron: Number(f.daily_ron), source: init?.theme ? "ai" : "wizard" }, { timeout: 300000 }); setDone(r.data); toast.success("Campanie creată pe pauză");
      if (init?.rec) axios.post(`${MA}/recommendations/${init.rec[0]}/${init.rec[1]}/done`).catch(() => {}); }
    catch (e) { toast.error(e.response?.data?.detail || e.message, { duration: 12000 }); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="meta-wizard">
        <DialogHeader><DialogTitle>Campanie nouă Meta (se creează pe pauză)</DialogTitle></DialogHeader>
        {done ? (
          <div className="space-y-3" data-testid="meta-wizard-done">
            <p className="text-sm">Am creat „{done.name}”: campanie, set de reclame și reclamă — toate <b>pe pauză</b>. Meta verifică reclama (poate arăta „în revizuire”).</p>
            <a href={done.manager_url} target="_blank" rel="noreferrer" data-testid="meta-wizard-manager-link" className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 underline">Deschide în Ads Manager <ExternalLink className="h-3.5 w-3.5" /></a>
          </div>
        ) : !opt ? <Loader2 className="h-5 w-5 animate-spin" /> : (
          <div className="space-y-3">
            <Field label="Tip campanie">
              <select data-testid="meta-wizard-type" value={f.campaign_type} onChange={set("campaign_type")} className="h-9 w-full rounded-md border px-2 text-sm">
                {opt.types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2"><Field label="Nume"><Input data-testid="meta-wizard-name" value={f.name} onChange={set("name")} /></Field></div>
              <Field label="Buget zilnic (lei)"><Input data-testid="meta-wizard-budget" type="number" min={5} value={f.daily_ron} onChange={set("daily_ron")} /></Field>
            </div>
            <Field label="Link (produs, landing page sau site)">
              <div className="flex gap-2">
                <select data-testid="meta-wizard-link-select" value={links.some((l) => l.url === f.link) ? f.link : ""} onChange={(e) => e.target.value && setF({ ...f, link: e.target.value })} className="h-9 w-64 shrink-0 rounded-md border px-2 text-sm">
                  <option value="">Alege landing page…</option>{links.map((l) => <option key={l.url} value={l.url}>{l.label}</option>)}
                </select>
                <Input data-testid="meta-wizard-link" value={f.link} onChange={set("link")} />
              </div>
            </Field>
            {NEEDS_ASSET(f.campaign_type) ? <Field label="Poză sau video din Creative Ads"><AssetPicker value={f.asset_id} onChange={set("asset_id")} /></Field>
              : <p className="text-xs text-zinc-500">Reclama arată automat produsele din catalogul BRAGUS (121 produse), cu nume și preț.</p>}
            <Field label="Text principal"><Textarea data-testid="meta-wizard-text" rows={2} value={f.text} onChange={set("text")} /></Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Titlu"><Input data-testid="meta-wizard-headline" value={f.headline} onChange={set("headline")} /></Field>
              <Field label="Descriere"><Input data-testid="meta-wizard-description" value={f.description} onChange={set("description")} /></Field>
              <Field label="Buton"><select data-testid="meta-wizard-cta" value={f.cta} onChange={set("cta")} className="h-9 w-full rounded-md border px-2 text-sm">{opt.ctas.map((c) => <option key={c}>{c}</option>)}</select></Field>
            </div>
            <p className="text-[11px] text-zinc-500">Public: România, 18+, extins automat de Meta (Advantage+). La remarketing: vizitatorii site-ului din ultimele 30 de zile care nu au cumpărat. Optimizare pe achiziții prin pixel.</p>
            <button data-testid="meta-wizard-submit" onClick={submit} disabled={busy || (NEEDS_ASSET(f.campaign_type) && !f.asset_id)} className="flex w-full items-center justify-center gap-2 rounded-full bg-amber-400 py-2 text-sm font-semibold text-zinc-950 disabled:opacity-50">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Creează pe pauză</button>
          </div>)}
      </DialogContent>
    </Dialog>
  );
};
